import { cn } from '@utils/cn';
import React, { useMemo, useState } from 'react';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Pencil, Percent, Plus, Star, Trash2 } from 'lucide-react';
import {
  BudgetItemTaskGroupHeader,
  BudgetItemTaskGroupVariant,
} from '@components/molecules/BudgetItemTaskGroupHeader';
import { ChargedToInvoiceBadge } from '@components/molecules/ChargedToInvoiceBadge';
import {
  AvailableBudgetItem,
  AvailableBudgetItemsResult,
  InvoiceLineItemSourceType,
} from '@models/invoice.model';

type CategoryKey = 'all' | 'materials' | 'equipment' | 'expenses' | 'delegated';

interface ItemGroup {
  key: string;
  title: string;
  variant: BudgetItemTaskGroupVariant;
  items: AvailableBudgetItem[];
  subtotal: number;
}

const LINE_ITEMS_GROUP_KEY = '__line-items__';

const buildGroups = (rows: AvailableBudgetItem[]): ItemGroup[] => {
  const groups = new Map<string, ItemGroup>();
  for (const row of rows) {
    let key: string;
    let title: string;
    let variant: BudgetItemTaskGroupVariant;
    if (row.sourceType === InvoiceLineItemSourceType.LINE_ITEM) {
      key = LINE_ITEMS_GROUP_KEY;
      title = 'Project line items';
      variant = 'line-items';
    } else if (row.sourceType === InvoiceLineItemSourceType.DELEGATED_TASK_COST) {
      key = `delegated:${row.taskId || row.sourceEntityId}`;
      title = row.taskName || 'Delegated task';
      variant = 'delegated';
    } else {
      key = `task:${row.taskId || 'untasked'}`;
      title = row.taskName || 'Untasked items';
      variant = 'task';
    }
    let group = groups.get(key);
    if (!group) {
      group = { key, title, variant, items: [], subtotal: 0 };
      groups.set(key, group);
    }
    group.items.push(row);
    group.subtotal += Number(row.total) || 0;
  }
  const variantOrder: Record<BudgetItemTaskGroupVariant, number> = {
    task: 0,
    delegated: 1,
    'line-items': 2,
  };
  return Array.from(groups.values()).sort((a, b) => {
    if (variantOrder[a.variant] !== variantOrder[b.variant]) {
      return variantOrder[a.variant] - variantOrder[b.variant];
    }
    return a.title.localeCompare(b.title);
  });
};

interface InvoiceBudgetItemPickerProps {
  data?: AvailableBudgetItemsResult | null;
  selectedSourceIds: Set<string>;
  preSelectedSourceIds: Set<string>;
  onToggleSelection: (item: AvailableBudgetItem) => void;
  onSelectAllAvailable?: () => void;
  onResetToDefaults?: () => void;
  /**
   * Triggered when the user clicks the pencil icon on a LINE_ITEM row.
   * Only LINE_ITEM rows expose edit/delete — materials/equipment/delegated rows
   * are owned by other parts of the system and shouldn't be mutated from here.
   */
  onEditLineItem?: (item: AvailableBudgetItem) => void;
  onDeleteLineItem?: (item: AvailableBudgetItem) => void;
  /**
   * Triggered when the user clicks the "Add Line Item" button next to the search input.
   * The button only renders when this handler is provided.
   */
  onAddLineItem?: () => void;
  isAddingLineItem?: boolean;
  /**
   * Triggered when the user clicks the "%" action on a selected MATERIAL / EQUIPMENT row.
   * The action only renders for selected rows of those two source types — line items use
   * their own per-source discount via `onEditLineItem`, and delegated task costs are
   * intentionally excluded for now.
   */
  onEditDiscount?: (item: AvailableBudgetItem) => void;
  /**
   * Map sourceEntityId → total dollar discount currently applied on the cart entry for
   * that row. Used to render the discount badge for MATERIAL / EQUIPMENT rows whose
   * discount is per-invoice (not persisted on the source DTO).
   */
  cartDiscountBySourceId?: Map<string, number>;
  loading?: boolean;
  className?: string;
}

const CATEGORY_TABS: Array<{ key: CategoryKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'materials', label: 'Mat & Equip' },
  { key: 'delegated', label: 'Delegated' },
  { key: 'expenses', label: 'Line Items' },
];

const formatCurrency = (value: number): string => {
  return `$ ${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const CATEGORY_PILL_CLASSES: Record<InvoiceLineItemSourceType, string> = {
  [InvoiceLineItemSourceType.TASK_MATERIAL]: 'bg-blue-100 text-blue-700',
  [InvoiceLineItemSourceType.TASK_EQUIPMENT]: 'bg-gray-100 text-gray-700',
  [InvoiceLineItemSourceType.DELEGATED_TASK_COST]: 'bg-orange-100 text-orange-700',
  [InvoiceLineItemSourceType.LINE_ITEM]: 'bg-purple-100 text-purple-700',
};

const CATEGORY_PILL_LABEL: Record<InvoiceLineItemSourceType, string> = {
  [InvoiceLineItemSourceType.TASK_MATERIAL]: 'Material',
  [InvoiceLineItemSourceType.TASK_EQUIPMENT]: 'Equipment',
  [InvoiceLineItemSourceType.DELEGATED_TASK_COST]: 'Delegated',
  [InvoiceLineItemSourceType.LINE_ITEM]: 'Line Item',
};

const matchesCategory = (item: AvailableBudgetItem, category: CategoryKey): boolean => {
  if (category === 'all') return true;
  if (category === 'materials') {
    return (
      item.sourceType === InvoiceLineItemSourceType.TASK_MATERIAL ||
      item.sourceType === InvoiceLineItemSourceType.TASK_EQUIPMENT
    );
  }
  if (category === 'equipment') return item.sourceType === InvoiceLineItemSourceType.TASK_EQUIPMENT;
  if (category === 'delegated') return item.sourceType === InvoiceLineItemSourceType.DELEGATED_TASK_COST;
  if (category === 'expenses') return item.sourceType === InvoiceLineItemSourceType.LINE_ITEM;
  return false;
};

export const InvoiceBudgetItemPicker: React.FC<InvoiceBudgetItemPickerProps> = ({
  data,
  selectedSourceIds,
  preSelectedSourceIds,
  onToggleSelection,
  onSelectAllAvailable,
  onResetToDefaults,
  onEditLineItem,
  onDeleteLineItem,
  onAddLineItem,
  isAddingLineItem = false,
  onEditDiscount,
  cartDiscountBySourceId,
  loading = false,
  className = '',
}) => {
  const supportsLineItemActions = Boolean(onEditLineItem || onDeleteLineItem || onEditDiscount);
  const [activeTab, setActiveTab] = useState<CategoryKey>('all');
  const [search, setSearch] = useState('');
  const [hideLocked, setHideLocked] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allRows = useMemo<AvailableBudgetItem[]>(() => {
    if (!data) return [];
    return [
      ...(data.materials ?? []),
      ...(data.equipment ?? []),
      ...(data.delegatedTaskCosts ?? []),
      ...(data.expenses ?? []),
    ];
  }, [data]);

  const counts = useMemo(() => {
    return {
      all: allRows.length,
      materials: allRows.filter((r) => matchesCategory(r, 'materials')).length,
      delegated: allRows.filter((r) => matchesCategory(r, 'delegated')).length,
      expenses: allRows.filter((r) => matchesCategory(r, 'expenses')).length,
    } as Record<CategoryKey, number>;
  }, [allRows]);

  const filteredRows = useMemo(() => {
    let rows = allRows.filter((r) => matchesCategory(r, activeTab));
    if (hideLocked) rows = rows.filter((row) => row.isAvailableForInvoice);
    if (search.trim().length > 0) {
      const needle = search.trim().toLowerCase();
      rows = rows.filter(
        (row) =>
          row.description.toLowerCase().includes(needle) ||
          (row.taskName && row.taskName.toLowerCase().includes(needle)),
      );
    }
    return rows;
  }, [allRows, activeTab, hideLocked, search]);

  const groups = useMemo(() => buildGroups(filteredRows), [filteredRows]);

  const renderRow = (item: AvailableBudgetItem) => {
    const isSelected = selectedSourceIds.has(item.sourceEntityId);
    const isPreSelected = preSelectedSourceIds.has(item.sourceEntityId);
    const locked = !item.isAvailableForInvoice && !isSelected;
    const pillClass = CATEGORY_PILL_CLASSES[item.sourceType];
    const pillLabel = CATEGORY_PILL_LABEL[item.sourceType];

    // The DTO's `discountAmount` reflects the source-persistent discount (only
    // line items have one today). When the row lives in the cart, a per-invoice
    // override may be set — that wins over the source value.
    const cartDiscount = cartDiscountBySourceId?.get(item.sourceEntityId);
    const hasCartDiscount = Number(cartDiscount ?? 0) > 0;
    const effectiveDiscountAmount = hasCartDiscount
      ? Number(cartDiscount)
      : Number(item.discountAmount ?? 0);
    const gross = Number((item.quantity * item.unitPrice).toFixed(2));
    const effectiveTotal = Number(
      Math.max(0, gross - effectiveDiscountAmount).toFixed(2),
    );

    const canEditDiscount =
      Boolean(onEditDiscount) &&
      isSelected &&
      !locked &&
      (item.sourceType === InvoiceLineItemSourceType.TASK_MATERIAL ||
        item.sourceType === InvoiceLineItemSourceType.TASK_EQUIPMENT);

    return (
      <tr
        key={`${item.sourceType}-${item.sourceEntityId}`}
        className={cn(
          'border-b border-gray-100',
          locked && 'bg-gray-50/70',
          !locked && isPreSelected && isSelected && 'bg-amber-50/40',
          !locked && 'hover:bg-blue-50/40 cursor-pointer',
        )}
        onClick={locked ? undefined : () => onToggleSelection(item)}>
        <td className="px-3 py-2 w-10">
          <input
            type="checkbox"
            checked={isSelected}
            disabled={locked}
            onChange={() => onToggleSelection(item)}
            onClick={(e) => e.stopPropagation()}
            className={cn('w-4 h-4', locked && 'opacity-40')}
          />
        </td>
        <td className="px-3 py-2">
          <p className={cn('text-sm', locked ? 'text-gray-400 line-through' : 'text-gray-900', !locked && isSelected && 'font-medium')}>
            {item.description}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {isPreSelected && !locked && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800">
                <Star className="w-3 h-3" />
                Pre-selected
              </span>
            )}
            {hasCartDiscount && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800"
                title="Discount applied on this invoice">
                Discount −{formatCurrency(Number(cartDiscount))}
              </span>
            )}
            {!hasCartDiscount && item.discountType && Number(item.discountValue) > 0 && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800"
                title={`Per-unit discount: ${
                  item.discountType === 'USD'
                    ? `$${Number(item.discountValue).toFixed(2)}`
                    : `${Number(item.discountValue).toFixed(2)}%`
                }`}>
                Discount{' '}
                {item.discountType === 'USD'
                  ? `−$${Number(item.discountValue).toFixed(2)}/u`
                  : `−${Number(item.discountValue).toFixed(2)}%`}
              </span>
            )}
            {locked && <ChargedToInvoiceBadge size="sm" />}
          </div>
        </td>
        <td className="px-3 py-2">
          <span
            className={cn(
              'inline-flex items-center px-2 py-0.5 rounded-full text-[11px]',
              pillClass,
              locked && 'opacity-50',
            )}>
            {pillLabel}
          </span>
        </td>
        <td className={cn('px-3 py-2 text-sm text-right whitespace-nowrap w-24', locked ? 'text-gray-400' : 'text-gray-700')}>
          {item.quantity} {item.unitOfMeasure}
        </td>
        <td className={cn('px-3 py-2 text-sm text-right whitespace-nowrap w-28', locked ? 'text-gray-400' : 'text-gray-700')}>
          {formatCurrency(item.unitPrice)}
        </td>
        <td className={cn('px-3 py-2 text-sm text-right font-medium whitespace-nowrap w-28', locked ? 'text-gray-400' : 'text-gray-900')}>
          {formatCurrency(effectiveTotal)}
          {effectiveDiscountAmount > 0 && !locked && (
            <span className="block text-[10px] font-normal text-amber-700">
              −{formatCurrency(effectiveDiscountAmount)} off
            </span>
          )}
        </td>
        {supportsLineItemActions && (
          <td className="px-3 py-2 w-24">
            <div className="flex items-center gap-1 justify-end">
              {canEditDiscount && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditDiscount?.(item);
                  }}
                  className={cn(
                    'p-1 rounded',
                    hasCartDiscount
                      ? 'text-amber-700 hover:text-amber-800'
                      : 'text-gray-500 hover:text-amber-700',
                  )}
                  aria-label="Edit line discount"
                  title={hasCartDiscount ? 'Edit line discount' : 'Add line discount'}>
                  <Percent className="w-3.5 h-3.5" />
                </button>
              )}
              {item.sourceType === InvoiceLineItemSourceType.LINE_ITEM && !locked && onEditLineItem && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditLineItem(item);
                  }}
                  className="p-1 text-gray-500 hover:text-blue-600 rounded"
                  aria-label="Edit line item"
                  title="Edit line item">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
              {item.sourceType === InvoiceLineItemSourceType.LINE_ITEM && !locked && onDeleteLineItem && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteLineItem(item);
                  }}
                  className="p-1 text-gray-500 hover:text-red-600 rounded"
                  aria-label="Delete line item"
                  title="Delete line item">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'px-2.5 py-1 rounded-full text-xs border',
                activeTab === tab.key
                  ? 'bg-blue-50 border-blue-300 text-blue-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
              )}>
              {tab.label}
              <span className="ml-1 text-gray-400">{counts[tab.key]}</span>
            </button>
          ))}
          <span className="text-gray-300 mx-1">|</span>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={hideLocked}
              onChange={(e) => setHideLocked(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Hide locked
          </label>
        </div>
        <div className="flex items-center gap-3">
          {onResetToDefaults && (
            <button
              type="button"
              onClick={onResetToDefaults}
              className="text-xs text-gray-500 hover:text-gray-700">
              Reset to defaults
            </button>
          )}
          {onSelectAllAvailable && (
            <button
              type="button"
              onClick={onSelectAllAvailable}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium">
              Select all available
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded">
          <SearchOutlinedIcon className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description or task…"
            className="flex-1 text-sm focus:outline-none bg-transparent"
          />
        </div>
        {onAddLineItem && (
          <button
            type="button"
            onClick={onAddLineItem}
            disabled={isAddingLineItem}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium text-white bg-blue-900 hover:bg-blue-800 disabled:opacity-50 whitespace-nowrap">
            <Plus className="w-4 h-4" />
            {isAddingLineItem ? 'Adding…' : 'Add Line Item'}
          </button>
        )}
      </div>

      <div className="border border-gray-200 rounded overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 w-10"></th>
              <th className="px-3 py-2 text-left text-xs uppercase tracking-wide text-gray-500">Item</th>
              <th className="px-3 py-2 text-left text-xs uppercase tracking-wide text-gray-500">Category</th>
              <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 w-24">Qty</th>
              <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 w-28">Unit price</th>
              <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 w-28">Total</th>
              {supportsLineItemActions && (
                <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 w-24">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={supportsLineItemActions ? 7 : 6} className="px-3 py-6 text-center text-sm text-gray-400">
                  Loading budget items…
                </td>
              </tr>
            )}
            {!loading && groups.length === 0 && (
              <tr>
                <td colSpan={supportsLineItemActions ? 7 : 6} className="px-3 py-6 text-center text-sm text-gray-400">
                  No budget items match the current filters.
                </td>
              </tr>
            )}
            {!loading &&
              groups.map((group) => {
                const isExpanded = !collapsedGroups[group.key];
                return (
                  <React.Fragment key={group.key}>
                    <tr>
                      <td colSpan={supportsLineItemActions ? 7 : 6} className="p-0">
                        <BudgetItemTaskGroupHeader
                          title={group.title}
                          itemCount={group.items.length}
                          subtotal={group.subtotal}
                          variant={group.variant}
                          isExpanded={isExpanded}
                          onToggle={() => toggleGroup(group.key)}
                        />
                      </td>
                    </tr>
                    {isExpanded && group.items.map(renderRow)}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
